// src/reimbursements.js
// "Reimbursements owed to you" (Reece, 10/10/26): "I want to be able to make a
// note that I need to be reimbursed for credit report, or appraisal, or things
// like that." Money he paid out of pocket on a file and is owed back.
//
// Pure helpers — no React, no Supabase. The list lives on the loan as
// `loan.reimbursements` and is saved inside follow_ups._wf (lib.js wfPack /
// loanFromDb); App.jsx's mergeLoanRow merges it BY ITEM ID (newest updatedAt
// wins per item, an item only on one side is kept), so two devices or a stale
// tab never drop each other's items. Admin-only: api/borrower/loan.js strips
// _wf from what the borrower portal gets.
//
// Item: {id ("RB-<time36>-<rand36>"), kind, amount (dollars, cents kept),
//        paidOn ("YYYY-MM-DD", browser-local day), note, fileName?, filePath?
//        (loan-documents: <loanId>/reimbursements/<id>-<safe name>),
//        reimbursedOn ("YYYY-MM-DD" | null), createdAt, updatedAt,
//        deletedAt (ISO | null)}
// Items are NEVER removed from the list: Delete stamps deletedAt (a soft
// delete), so a stale tab's copy can neither resurrect it nor lose it — the
// newer stamp wins in the merge.

var REIMB_KINDS=["Credit report","Appraisal","Flood cert","Title / other fee","Other"];

// ── dates / stamps ──
function pad2(n){return String(n).padStart(2,"0");}
// Browser-LOCAL calendar day (never toISOString().slice(0,10) — that is the
// UTC day, already tomorrow on a Pacific evening).
function todayLocal(now){var d=now instanceof Date?now:new Date();return d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());}
var DAY_RE=/^(\d{4})-(\d{2})-(\d{2})$/;
function isDay(s){var m=DAY_RE.exec(String(s||""));if(!m)return false;var d=new Date(+m[1],+m[2]-1,+m[3],12);return d.getFullYear()===+m[1]&&d.getMonth()===+m[2]-1&&d.getDate()===+m[3];}
// "2026-10-08" → "10/8/2026" (anything else as given).
function fmtDay(s){var m=DAY_RE.exec(String(s||""));return m?(+m[2])+"/"+(+m[3])+"/"+m[1]:String(s||"");}
function msOf(v){var t=Date.parse(v);return isFinite(t)?t:0;}
// A local change is always newer than the version it changed — even when the
// other device's clock ran ahead and stamped that version "in the future".
function stampAfter(prevIso,now){var n=now instanceof Date?now.getTime():(now!=null&&isFinite(Date.parse(now))?Date.parse(now):Date.now());return new Date(Math.max(n,msOf(prevIso)+1)).toISOString();}
function nowIso(now){return now instanceof Date?now.toISOString():(now!=null&&isFinite(Date.parse(now))?new Date(Date.parse(now)).toISOString():new Date().toISOString());}

// ── ids ──
function rand36(n){
  var s="";
  try{
    var c=typeof window!=="undefined"?window.crypto:null;
    if(c&&typeof c.getRandomValues==="function"){var a=new Uint32Array(n);c.getRandomValues(a);for(var i=0;i<n;i++)s+=(a[i]%36).toString(36);return s;}
  }catch(e){/* fall back below */}
  while(s.length<n)s+=Math.random().toString(36).slice(2);
  return s.slice(0,n);
}
// A fresh id ("RB-<time36>-<rand36>"), never one already in `list` — never
// length-based (a delete would make the next add reuse an id).
function newReimbId(list){
  var taken=Object.create(null);(Array.isArray(list)?list:[]).forEach(function(x){if(x&&x.id)taken[x.id]=1;});
  for(var i=0;i<20;i++){var id="RB-"+Date.now().toString(36)+"-"+rand36(8);if(!taken[id])return id;}
  return "RB-"+Date.now().toString(36)+"-"+rand36(16);
}

// ── money ──
// Dollars with the cents kept (a $65.50 credit report is $65.50). Junk → 0.
function toAmount(v){
  var n=typeof v==="number"?v:parseFloat(String(v==null?"":v).replace(/[^0-9.-]/g,""));
  if(!isFinite(n)||n<0)return 0;
  return Math.round(n*100)/100;
}
function sumAmounts(items){return Math.round((items||[]).reduce(function(a,x){return a+toAmount(x&&x.amount);},0)*100)/100;}
// "$550" / "$65.50" / "$1,234.05".
function fmtMoney(n){
  var v=toAmount(n);var cents=Math.round(v*100)%100!==0;
  return "$"+v.toLocaleString("en-US",{minimumFractionDigits:cents?2:0,maximumFractionDigits:2});
}

// ── reading ──
function listOf(x){if(Array.isArray(x))return x;return(x&&Array.isArray(x.reimbursements))?x.reimbursements:[];}
// One stored item → the shape the app works with (a row from another device
// or an older copy can be missing fields). null for junk (no id).
function normItem(r){
  if(!r||typeof r!=="object"||!r.id)return null;
  var kind=String(r.kind||"");if(!kind)kind="Other";
  return{id:String(r.id),kind:kind,amount:toAmount(r.amount),paidOn:r.paidOn?String(r.paidOn):"",note:r.note?String(r.note):"",
    fileName:r.fileName?String(r.fileName):null,filePath:r.filePath?String(r.filePath):null,
    reimbursedOn:r.reimbursedOn?String(r.reimbursedOn):null,createdAt:r.createdAt||null,updatedAt:r.updatedAt||null,deletedAt:r.deletedAt||null};
}
// wfPack / loanFromDb: the list as stored (tombstones included).
function packReimbursements(list){return listOf(list).map(normItem).filter(Boolean);}
function unpackReimbursements(raw){return Array.isArray(raw)?raw.map(normItem).filter(Boolean):[];}

function isLive(x){return!!(x&&x.id&&!x.deletedAt);}
function byPaidOn(a,b){return String(a.paidOn||"9999").localeCompare(String(b.paidOn||"9999"))||String(a.createdAt||"").localeCompare(String(b.createdAt||""));}
// Every item not deleted, oldest Date paid first.
function liveItems(loanOrList){return listOf(loanOrList).filter(isLive).slice().sort(byPaidOn);}
// Still owed: not deleted, not marked reimbursed — oldest Date paid first.
function owedItems(loanOrList){return liveItems(loanOrList).filter(function(x){return!x.reimbursedOn;});}
function reimbursedItems(loanOrList){return liveItems(loanOrList).filter(function(x){return!!x.reimbursedOn;}).sort(function(a,b){return String(b.reimbursedOn).localeCompare(String(a.reimbursedOn));});}
function owedTotal(loanOrList){return sumAmounts(owedItems(loanOrList));}
function reimbursedTotal(loanOrList){return sumAmounts(reimbursedItems(loanOrList));}
// Command Center: every file with something owed — oldest Date paid first —
// with its items and total, plus the grand total.
function owedByFile(loans){
  var files=[];
  (loans||[]).forEach(function(l){
    var items=owedItems(l);if(!items.length)return;
    files.push({loan:l,items:items,total:sumAmounts(items),oldest:items[0].paidOn||""});
  });
  files.sort(function(a,b){return String(a.oldest||"9999").localeCompare(String(b.oldest||"9999"))||String(a.loan.id).localeCompare(String(b.loan.id));});
  return{files:files,total:Math.round(files.reduce(function(a,f){return a+f.total;},0)*100)/100};
}

// ── writing (each returns a NEW array; the input is never mutated) ──
// fields: {id?, kind, amount, paidOn, note, fileName?, filePath?}. A missing
// or invalid Date paid is today (local).
function addItem(list,fields,now){
  var arr=listOf(list);var f=fields||{};
  var at=nowIso(now);
  var id=f.id&&!arr.some(function(x){return x&&x.id===f.id;})?String(f.id):newReimbId(arr);
  var item={id:id,kind:REIMB_KINDS.indexOf(f.kind)>=0?f.kind:(f.kind?String(f.kind):REIMB_KINDS[0]),amount:toAmount(f.amount),
    paidOn:isDay(f.paidOn)?f.paidOn:todayLocal(now instanceof Date?now:undefined),note:String(f.note||"").trim(),
    fileName:f.fileName||null,filePath:f.filePath||null,reimbursedOn:null,createdAt:at,updatedAt:at,deletedAt:null};
  return arr.concat([item]);
}
var EDITABLE=["kind","amount","paidOn","note","fileName","filePath"];
// Only the editable fields; nothing actually different → the SAME array (no save).
function updateItem(list,id,patch,now){
  var arr=listOf(list);var p=patch||{};var hit=false;
  var out=arr.map(function(x){
    if(!x||x.id!==id||x.deletedAt)return x;
    var n=Object.assign({},x);var changed=false;
    EDITABLE.forEach(function(k){
      if(!Object.prototype.hasOwnProperty.call(p,k))return;
      var v=p[k];
      if(k==="amount")v=toAmount(v);
      else if(k==="paidOn"){if(!isDay(v))return;}
      else if(k==="note")v=String(v||"").trim();
      else if(k==="fileName"||k==="filePath")v=v?String(v):null;
      else if(k==="kind")v=String(v||"")||"Other";
      if(n[k]!==v){n[k]=v;changed=true;}
    });
    if(!changed)return x;
    hit=true;n.updatedAt=stampAfter(x.updatedAt,now);return n;
  });
  return hit?out:arr;
}
// on: "YYYY-MM-DD" (default today, local) — or null to undo.
function markReimbursed(list,id,on,now){
  var arr=listOf(list);
  var day=on===null?null:(isDay(on)?on:todayLocal(now instanceof Date?now:undefined));
  var hit=false;
  var out=arr.map(function(x){
    if(!x||x.id!==id||x.deletedAt)return x;
    if((x.reimbursedOn||null)===day)return x;
    hit=true;return Object.assign({},x,{reimbursedOn:day,updatedAt:stampAfter(x.updatedAt,now)});
  });
  return hit?out:arr;
}
function undoReimbursed(list,id,now){return markReimbursed(list,id,null,now);}
// Soft delete: the item stays in the list with deletedAt, so the merge keeps
// it deleted on every device.
function removeItem(list,id,now){
  var arr=listOf(list);var hit=false;
  var out=arr.map(function(x){
    if(!x||x.id!==id||x.deletedAt)return x;
    hit=true;var at=stampAfter(x.updatedAt,now);return Object.assign({},x,{deletedAt:at,updatedAt:at});
  });
  return hit?out:arr;
}
// Invoice files: <loanId>/reimbursements/<item id>-<safe name> in the
// loan-documents bucket (the item id makes it unguessable and unique).
function reimbursementPath(loanId,itemId,safeName){return String(loanId)+"/reimbursements/"+String(itemId)+"-"+String(safeName||"invoice");}

export { REIMB_KINDS, todayLocal, fmtDay, isDay, newReimbId, toAmount, fmtMoney, normItem, packReimbursements, unpackReimbursements,
  liveItems, owedItems, reimbursedItems, owedTotal, reimbursedTotal, owedByFile,
  addItem, updateItem, markReimbursed, undoReimbursed, removeItem, reimbursementPath };
